import { normalizeFreeTicketRequirements } from './freeTicketRequirements';
import { nftRowHasGoldenConfigured, nftRowHasNormalConfigured } from './nftBonusConfig';

/**
 * Build a structured list of blockers / hints for free play on an event.
 */
export function computeFreePlayEligibility({
  user,
  account,
  balances,
  balancesLoading = false,
  minFreeTickets = 1,
  minGoldenTickets = 0,
  locked = false,
  isResolved = false,
  nftBonuses = [],
  mode = 'create',
}) {
  const entryReqs = normalizeFreeTicketRequirements(minFreeTickets, minGoldenTickets);
  const items = [];
  let canEnter = true;

  if (isResolved) {
    canEnter = false;
    return { canEnter, items, entryReqs, primaryBlocker: null };
  }

  if (locked) {
    items.push({
      severity: 'error',
      code: 'locked',
      title: 'Predictions locked',
      message: 'This event is locked — free picks are no longer accepted.',
    });
    canEnter = false;
  }

  if (!user) {
    items.push({
      severity: 'warning',
      code: 'login',
      title: 'Log in required',
      message: 'Sign in to your account to place a free prediction.',
    });
    canEnter = false;
    return { canEnter, items, entryReqs, primaryBlocker: items[0] };
  }

  if (!user.emailVerified) {
    items.push({
      severity: 'warning',
      code: 'email',
      title: user.needsReverification ? 'Email verification expired' : 'Verify your email',
      message: user.needsReverification
        ? 'Re-verify your email to continue playing free predictions.'
        : 'Verify your email once to unlock free predictions (6-digit code).',
    });
    canEnter = false;
  }

  const normalHave = balancesLoading ? null : Math.max(0, balances?.normalTickets ?? 0);
  const goldenHave = balancesLoading ? null : Math.max(0, balances?.goldenTickets ?? 0);
  const totalHave =
    normalHave == null || goldenHave == null ? null : normalHave + goldenHave;
  const minTotal = entryReqs.minTotalStake;

  if (!balancesLoading && user?.emailVerified && !locked) {
    if (entryReqs.minFreeTickets > 0 && normalHave < entryReqs.minFreeTickets) {
      const short = entryReqs.minFreeTickets - normalHave;
      items.push({
        severity: 'error',
        code: 'normal_short',
        title: 'Not enough normal tickets',
        message: `You need ${entryReqs.minFreeTickets} normal ticket${entryReqs.minFreeTickets === 1 ? '' : 's'} to enter (you have ${normalHave}).`,
        need: entryReqs.minFreeTickets,
        have: normalHave,
        short,
      });
      canEnter = false;
    }

    if (entryReqs.minGoldenTickets > 0 && goldenHave < entryReqs.minGoldenTickets) {
      const short = entryReqs.minGoldenTickets - goldenHave;
      items.push({
        severity: 'error',
        code: 'golden_short',
        title: 'Not enough golden tickets',
        message: `You need ${entryReqs.minGoldenTickets} golden ticket${entryReqs.minGoldenTickets === 1 ? '' : 's'} to enter (you have ${goldenHave}). Earn golden tickets from boost, market, admin gifts, or NFT holder bonuses.`,
        need: entryReqs.minGoldenTickets,
        have: goldenHave,
        short,
      });
      canEnter = false;
    }

    if (mode === 'create' && totalHave != null && totalHave < minTotal && canEnter) {
      items.push({
        severity: 'error',
        code: 'total_short',
        title: 'Not enough tickets',
        message: `Minimum stake for this event is ${minTotal} ticket${minTotal === 1 ? '' : 's'} total.`,
        need: minTotal,
        have: totalHave,
        short: minTotal - totalHave,
      });
      canEnter = false;
    }
  }

  const nftGoldenConfigured = (nftBonuses || []).some((n) => nftRowHasGoldenConfigured(n));
  const nftNormalConfigured = (nftBonuses || []).some((n) => nftRowHasNormalConfigured(n));
  const hasLinkedWallet = !!(user?.wallets?.length || user?.walletAddress);

  if (user && (nftGoldenConfigured || nftNormalConfigured) && !hasLinkedWallet) {
    items.push({
      severity: 'info',
      code: 'wallet_nft',
      title: 'Link wallet for NFT bonuses',
      message: nftGoldenConfigured
        ? 'Link your wallet once to verify NFT holdings. Golden tickets accrue automatically while you hold eligible tokens — no need to reconnect each visit. Selling stops future golden grants.'
        : 'Link your wallet once to verify NFT holdings and receive bonus tickets automatically in the background.',
    });
  }

  if (user?.emailVerified && !locked && entryReqs.valid && (entryReqs.minFreeTickets > 0 || entryReqs.minGoldenTickets > 0)) {
    const reqParts = [];
    if (entryReqs.minFreeTickets > 0) reqParts.push(`${entryReqs.minFreeTickets} normal`);
    if (entryReqs.minGoldenTickets > 0) reqParts.push(`${entryReqs.minGoldenTickets} golden`);
    items.push({
      severity: 'info',
      code: 'entry_req',
      title: 'Entry requirement',
      message: `This event requires ${reqParts.join(' + ')} ticket${minTotal === 1 ? '' : 's'} minimum per pick.`,
    });
  }

  const primaryBlocker = items.find((i) => i.severity === 'error' || i.severity === 'warning') || null;

  return { canEnter: canEnter && user?.emailVerified, items, entryReqs, primaryBlocker };
}

export function freePlayBlockerMessage(eligibility) {
  if (!eligibility?.primaryBlocker) return null;
  return eligibility.primaryBlocker.message;
}
